import { useState, useEffect } from "react";
import { supabase } from "../../../../../integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Button } from "../../../../ui/button";
import { Download, RefreshCw, MessageSquare, Star, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import { Skeleton } from "../../../../ui/skeleton";
import { useToast } from "../../../../../hooks/use-toast";
import { SentimentChart } from "./overview/SentimentChart";
import { PlatformChart } from "./overview/PlatformChart";
import { VolumeChart } from "./overview/VolumeChart";
import { TopKeywords } from "./overview/TopKeywords";

type DateRange = "7days" | "30days" | "90days" | "all";

interface PerformanceOverviewProps {
  selectedOutlets?: string[];
  supabaseoutletIDs?: string[],
  onNavigateToUrgent?: () => void;
}

export const PerformanceOverview = ({ selectedOutlets, supabaseoutletIDs, onNavigateToUrgent }: PerformanceOverviewProps) => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>("all");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("all");
  const [stats, setStats] = useState({
    totalReviews: 0,
    avgRating: 0,
    positivePercent: 0,
    neutralPercent: 0,
    negativePercent: 0,
    urgentCount: 0,
  });
  const { toast } = useToast();

  useEffect(() => {
    loadData();
    analyzeUnanalyzedReviews();
  }, [dateRange, selectedPlatform, selectedOutlets]);

  const loadData = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("reviews")
        .select("*").in('outlet_id', supabaseoutletIDs);

      // Apply date filter only if not "all"
      if (dateRange !== "all") {
        const daysAgo = dateRange === "7days" ? 7 : dateRange === "30days" ? 30 : 90;
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - daysAgo);
        query = query.gte("posted_at", startDate.toISOString());
      }

      if (selectedPlatform !== "all") {
        query = query.eq("platform", selectedPlatform as any);
      }

      const isAllSelected = !selectedOutlets || selectedOutlets.length === 0 || selectedOutlets.includes("all");
      if (!isAllSelected && selectedOutlets.length > 0) {
        query = query.in("outlet_id", selectedOutlets);
      }else {
        query = query.in("outlet_id", supabaseoutletIDs);
      }

      const { data: reviews, error } = await query;

      if (error) throw error;

      // Calculate stats
      const total = reviews?.length || 0;
      const avgRating = reviews?.reduce((acc, r) => acc + (r.rating || 0), 0) / total || 0;
      const positive = reviews?.filter(r => r.sentiment === "positive").length || 0;
      const neutral = reviews?.filter(r => r.sentiment === "neutral").length || 0;
      const negative = reviews?.filter(r => r.sentiment === "negative").length || 0;
      const urgent = reviews?.filter(r => r.urgency_flag === true).length || 0;

      setStats({
        totalReviews: total,
        avgRating: Number(avgRating.toFixed(1)),
        positivePercent: total > 0 ? Math.round((positive / total) * 100) : 0,
        neutralPercent: total > 0 ? Math.round((neutral / total) * 100) : 0,
        negativePercent: total > 0 ? Math.round((negative / total) * 100) : 0,
        urgentCount: urgent,
      });
    } catch (error) {
      console.error("Error loading data:", error);
      toast({
        title: "Error loading data",
        description: "Failed to load performance data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadData();
    toast({
      title: "Data refreshed",
      description: "Performance data has been updated",
    });
  };

  const handleDownloadReport = async () => {
    toast({
      title: "Generating report",
      description: "Your PDF report is being prepared...",
    });
  };

  const analyzeUnanalyzedReviews = async () => {
    try {
      // Get all reviews without sentiment
      const { data: reviews, error } = await supabase
        .from("reviews")
        .select("id, platform_review_id, platform")
        .is("sentiment", null).in('outlet_id', supabaseoutletIDs);

      if (error || !reviews || reviews.length === 0) return;

      // Analyze each review in the background
      for (const review of reviews) {
        supabase.functions.invoke("analyze-review", {
          body: {
            reviewId: review.platform_review_id,
            platform: review.platform,
          },
        }).catch(err => console.error("Failed to analyze review:", err));
      }
    } catch (error) {
      console.error("Error analyzing reviews:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-bold">Performance Dashboard</h1>
          <p className="text-muted-foreground">Multi-Platform Review Summary & Trends</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRange)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Platforms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              <SelectItem value="google_maps">Google Maps</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="tiktok">TikTok</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
              <SelectItem value="tripadvisor">TripAdvisor</SelectItem>
            </SelectContent>
          </Select>

          <div className="ml-auto flex gap-2">
            {/* <Button variant="outline" size="sm" onClick={handleDownloadReport}>
              <Download className="mr-2 h-4 w-4" />
              Download Report
            </Button> */}
            <Button variant="outline" size="icon" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.totalReviews}</div>
                <p className="text-xs text-muted-foreground">in selected period</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.avgRating} ★</div>
                <p className="text-xs text-muted-foreground">Out of 5 stars</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Positive Sentiment</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold text-green-500">{stats.positivePercent}%</div>
                <p className="text-xs text-muted-foreground">of all reviews</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Neutral Sentiment</CardTitle>
            <TrendingUp className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold text-yellow-500">{stats.neutralPercent}%</div>
                <p className="text-xs text-muted-foreground">of all reviews</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Negative Sentiment</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold text-red-500">{stats.negativePercent}%</div>
                <p className="text-xs text-muted-foreground">of all reviews</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card
          className={onNavigateToUrgent ? "cursor-pointer hover:shadow-lg transition-shadow" : ""}
          onClick={onNavigateToUrgent}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Urgent Reviews</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold text-orange-500">{stats.urgentCount}</div>
                <p className="text-xs text-muted-foreground">
                  {onNavigateToUrgent ? "Click to manage →" : "need attention"}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <SentimentChart dateRange={dateRange} platform={selectedPlatform} outletIds={selectedOutlets} supabaseoutletIDs={supabaseoutletIDs}  />
        <PlatformChart dateRange={dateRange} outletIds={selectedOutlets} supabaseoutletIDs={supabaseoutletIDs}  />
        <VolumeChart dateRange={dateRange} platform={selectedPlatform} outletIds={selectedOutlets} supabaseoutletIDs={supabaseoutletIDs}  />
      </div>

      {/* Keywords & Topics */}
      <TopKeywords dateRange={dateRange} platform={selectedPlatform} outletIds={selectedOutlets} supabaseoutletIDs={supabaseoutletIDs} />
    </div>
  );
};
